import produce from 'immer';

import {assert, assertAndContinue} from 'Asserts';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import {readQuerySafe, writeFragmentsToCache, writeQuery} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {LookupConfigSpec} from 'lookup-config/LookupConfigModalConstants';
import notifications from 'notifications';
import {ProcessFlowchartConfigVersionSpec} from 'processes/dao/ProcessSchemaDAO';
import * as ProcessSchemaDAO from 'processes/dao/ProcessSchemaDAO';
import {AllProcessTaskSchemasForSchemaConfigQuery} from 'processes/modals/utils/__generated/AllProcessTaskSchemasForSchemaConfigQuery';
import {ALL_PROCESS_TASK_SCHEMAS_FOR_SCHEMA_CONFIG_QUERY} from 'processes/modals/utils/ProcessesAllSchemasQueryUtils';
import {ClientSchemaField} from 'registry/field/FieldTable';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {massageFieldsForSubmit} from 'schemas/SchemaUtil';
import {createSnapshotField, updateSnapshotField} from 'schemas/snapshot-field/SnapshotFieldDAO';
import {MinimalEntity} from 'ui-library/entity-input/EntityInputConstants';
import {withLoadingScreen} from 'util/Promises';

import {ProcessTaskSchemaEditorQuery} from './__generated/ProcessTaskSchemaEditorQuery';

type ProcessTaskSchemaGQL =
  AllProcessTaskSchemasForSchemaConfigQuery['processTaskSchemasSupportingPlatformFeature'][number];

export type ProcessTaskSchema = ExtractByTypename<
  ProcessTaskSchemaEditorQuery['processTaskSchema'],
  'ProcessTaskSchemaNode'
>;

export function isEmptyOrWhitespace(str: string): RegExpMatchArray | null {
  return str.match(/^\s*$/g);
}

export function insertOrReplaceTaskSchema(
  taskSchemas: Array<ProcessTaskSchemaGQL>,
  taskSchemaToInsert: ProcessTaskSchemaGQL
): Array<ProcessTaskSchemaGQL> {
  return produce(taskSchemas, (draftTaskSchemas) => {
    const schemaIdx = draftTaskSchemas.findIndex(({id}) => taskSchemaToInsert.id === id);
    if (schemaIdx < 0) {
      draftTaskSchemas.push(taskSchemaToInsert);
    } else {
      draftTaskSchemas[schemaIdx] = taskSchemaToInsert;
    }
  });
}

export interface UpdateProcessSchema {
  taskGroupPrefix?: string;
  name?: string;
  sqlIdentifier?: string;
  taskSchemaPrefix?: string;
  outputSchemaPrefix?: string;
  taskFields?: Array<ClientSchemaField>;
  outputFields?: Array<ClientSchemaField>;
  autopopulateDirectOutputsFromTasks?: boolean;
  defaultResponsibleParties?: Array<MinimalEntity>;
  responsiblePartyLookupConfig?: LookupConfigSpec | null;
  taskFinderLookupConfig?: LookupConfigSpec | null;
  updateTaskFinder?: boolean;
  canSetAssigneeOnTaskCreation?: boolean;
  canOnlyBePartOfFlowchart?: boolean;
  defaultCreationFolderId?: string | null;
  defaultEntryExecutionFolderId?: string | null;
  taskGroupInstructions?: string | null;
  isPropagateWatchersEnabled?: boolean;
  flowchartConfig?: ProcessFlowchartConfigVersionSpec | null;
  configEditorEventGroupId?: string | null;
  didInitialFlowchartHaveChainedRouters?: boolean;
}

export type FieldsKey = 'fields' | 'taskFields' | 'outputFields';

export async function createOrUpdateSnapshotField(
  changedFields: Array<ClientSchemaField | SchemaField>,
  newFields: Array<ClientSchemaField>,
  fieldsKey: FieldsKey,
  setErrors: (errors: Record<string, string>) => void,
  setStateOnUpdate?: (params: UpdateProcessSchema & {updateTables?: boolean}) => void,
  refetchProcessSchema?: () => void,
  processTaskSchema?: ProcessTaskSchema | null
): Promise<void> {
  if (setStateOnUpdate) {
    setStateOnUpdate({
      [fieldsKey]: newFields,
    } as UpdateProcessSchema);
  }
  assertAndContinue(changedFields.length === 1, 'Expected only one created/update snapshot field');
  const snapshotField = changedFields[0];
  const schemaId = fieldsKey === 'taskFields' ? processTaskSchema!.id : processTaskSchema!.outputSchema!.id;
  const saveSnapshotField = async (): Promise<void> => {
    try {
      if (!snapshotField.apiId) {
        await createSnapshotField({
          schemaId,
          displayName: snapshotField.displayName,
          systemName: snapshotField.systemName,
          lookupConfigSpec: snapshotField.lookupConfig!,
        });
      } else {
        await updateSnapshotField({
          snapshotFieldId: snapshotField.apiId,
          displayName: snapshotField.displayName,
          systemName: snapshotField.systemName,
          lookupConfigSpec: snapshotField.lookupConfig!,
          archiveRecord: snapshotField.archiveRecord,
        });
      }
      assert(refetchProcessSchema !== undefined, 'Expected defined refetchProcessSchema prop');
      refetchProcessSchema();
      if (setStateOnUpdate) {
        setStateOnUpdate({
          taskFields: processTaskSchema!.allFields,
          outputFields: processTaskSchema!.outputSchema?.allFields ?? null,
        } as UpdateProcessSchema);
      }
      notifications.success('Updated schema successfully');
      // eslint-disable-next-line benchlint/always-throw-from-catch
    } catch (e) {
      if (e.responseJSON != null) {
        setErrors(e.responseJSON.errors ?? {});
      }
      notifications.notifyError(e);
    }
  };

  await withLoadingScreen(saveSnapshotField());
}

const SAVING_TASK_SCHEMA_MESSAGE = 'Saving...';

export async function updateProcessTaskSchema(
  params: UpdateProcessSchema,
  setStateOnUpdate: (params: UpdateProcessSchema & {updateTables?: boolean}) => void,
  setErrors: (errors: Record<string, string>) => void,
  oldProcessTaskSchema: ProcessTaskSchema | null,
  hasOutputSchema: boolean,
  refetchProcessSchema?: () => void,
  setIsEditingFlowchart?: (isEditing: boolean) => void
): Promise<void> {
  // Optimistically update component state
  setStateOnUpdate(params);
  setErrors({});
  if (oldProcessTaskSchema == null) {
    return;
  }
  try {
    // TODO: add default responsible parties to update payload
    const processTaskSchema = await withLoadingScreen(
      ProcessSchemaDAO.updateProcessTaskSchema({
        schemaId: oldProcessTaskSchema.id,
        ...(params.taskGroupPrefix != null ? {taskGroupPrefix: params.taskGroupPrefix} : {}),
        ...(params.name != null ? {name: params.name} : {}),
        ...(params.sqlIdentifier != null ? {sqlIdentifier: params.sqlIdentifier} : {}),
        ...(params.taskSchemaPrefix != null ? {prefix: params.taskSchemaPrefix} : {}),
        ...(params.canSetAssigneeOnTaskCreation != null
          ? {canSetAssigneeOnTaskCreation: params.canSetAssigneeOnTaskCreation}
          : {}),
        ...(params.canOnlyBePartOfFlowchart != null
          ? {canOnlyBePartOfFlowchart: params.canOnlyBePartOfFlowchart}
          : {}),
        defaultResponsiblePartiesIds:
          params.defaultResponsibleParties === undefined
            ? undefined
            : params.defaultResponsibleParties.map(({id}) => id),
        responsiblePartyLookupConfigs:
          params.responsiblePartyLookupConfig === undefined
            ? undefined
            : params.responsiblePartyLookupConfig == null
              ? null
              : [params.responsiblePartyLookupConfig],
        ...(params.taskFields != null ? {schemaFields: massageFieldsForSubmit(params.taskFields)} : {}),
        processOutputSchema: hasOutputSchema
          ? {
              ...(params.outputSchemaPrefix != null ? {prefix: params.outputSchemaPrefix} : {}),
              ...(params.outputFields != null
                ? {schemaFields: massageFieldsForSubmit(params.outputFields)}
                : {}),
              ...(params.updateTaskFinder ? {taskFinderLookupConfig: params.taskFinderLookupConfig} : {}),
              ...(params.autopopulateDirectOutputsFromTasks != null
                ? {autopopulateFromTasks: params.autopopulateDirectOutputsFromTasks}
                : {}),
            }
          : null,
        defaultCreationFolderId: params.defaultCreationFolderId,
        defaultEntryExecutionFolderId: params.defaultEntryExecutionFolderId,
        ...(params.flowchartConfig != null ? {flowchartConfig: params.flowchartConfig} : {}),
        taskGroupInstructions: params.taskGroupInstructions,
        ...(params.isPropagateWatchersEnabled != null
          ? {isPropagateWatchersEnabled: params.isPropagateWatchersEnabled}
          : {}),
        configEditorEventGroupId: params.configEditorEventGroupId,
        didInitialFlowchartHaveChainedRouters: params.didInitialFlowchartHaveChainedRouters ?? false,
        actionOrigin: ActionOrigin.SETTINGS_TASK_SCHEMA_PAGE,
      }),
      SAVING_TASK_SCHEMA_MESSAGE
    );
    cacheUpdatedTaskSchema(processTaskSchema);
    // Update state with field table from server on success
    setStateOnUpdate({
      taskFields: processTaskSchema.allFields,
      outputFields: processTaskSchema.outputSchema?.allFields ?? null,
    } as UpdateProcessSchema);
    if (refetchProcessSchema != null) {
      refetchProcessSchema();
    }
    notifications.success('Updated schema successfully');
    // eslint-disable-next-line benchlint/always-throw-from-catch
  } catch (e) {
    if (e.responseJSON != null) {
      setErrors(e.responseJSON.errors ?? {});
    }
    // Revert field state to match server on failure
    setStateOnUpdate({
      taskFields: oldProcessTaskSchema.allFields,
      outputFields: oldProcessTaskSchema.outputSchema?.allFields ?? null,
      updateTables: true,
    });
    // If the flowchart config was being updated we should put it back into editing mode.
    if (params.flowchartConfig != null && setIsEditingFlowchart != null) {
      setIsEditingFlowchart(true);
    }
    notifications.notifyError(e);
  }
}

export function cacheUpdatedTaskSchema(updatedTaskSchema: ProcessTaskSchemaGQL): void {
  const processTaskSchemaResult = readQuerySafe<AllProcessTaskSchemasForSchemaConfigQuery>({
    query: ALL_PROCESS_TASK_SCHEMAS_FOR_SCHEMA_CONFIG_QUERY,
  });
  if (processTaskSchemaResult != null) {
    const taskSchemasWithUpdate = insertOrReplaceTaskSchema(
      processTaskSchemaResult.processTaskSchemasSupportingPlatformFeature,
      updatedTaskSchema
    );
    writeQuery(
      {query: ALL_PROCESS_TASK_SCHEMAS_FOR_SCHEMA_CONFIG_QUERY},
      {
        ...processTaskSchemaResult,
        processTaskSchemasSupportingPlatformFeature: taskSchemasWithUpdate,
        __typename: 'Query',
      }
    );
    writeFragmentsToCache(getFragmentWrapper('AllProcessTaskSchemasForSchemaConfig'), [
      {processTaskSchemasSupportingPlatformFeature: taskSchemasWithUpdate, __typename: 'Query'},
    ]);
  }

  writeFragmentsToCache(getFragmentWrapper('ProcessTaskSchema'), [updatedTaskSchema]);
}
